import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Empty,
  Input,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType, TableProps } from "antd/es/table";
import dayjs from "dayjs";
import {
  getMeasurementsForAssetOptions,
  getOcppConfigurationOptions,
  getTransactionsWithEventsForSiteInfiniteOptions,
} from "../../../services/client/@tanstack/react-query.gen";
import type {
  Measurement,
  TransactionViewDto,
} from "../../../services/client/types.gen";
import { FETCH_PAGE_SIZE } from "../../../hooks/useCursorPagination";
import { extractErrorMessage } from "../../../util/extractErrorMessage";
import { MESSAGE_TIME_FORMAT } from "./constants";
import {
  DATA_TYPES,
  type DataType,
  isDataType,
  useOcppMessageFilters,
} from "./useOcppMessageFilters";
import { MessageRangeFilter } from "./MessageRangeFilter";

const DATA_TYPE_TAGS: Record<DataType, { label: string; color: string }> = {
  measurements: { label: "Measurement", color: "blue" },
  transactions: { label: "Transaction", color: "purple" },
};

type Row =
  | {
      key: string;
      type: "measurements";
      timestamp: string;
      asset_id: string;
      measurement: Measurement;
    }
  | {
      key: string;
      type: "transactions";
      timestamp: string;
      asset_id: string;
      transaction: TransactionViewDto;
    };

const formatValue = (value: number | null | undefined, unit: string) =>
  value === null || value === undefined
    ? null
    : `${(value / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unit}`;

const summarise = (row: Row): string[] => {
  if (row.type === "measurements") {
    const { powerValue, energyValue, currentStateOfCharge } = row.measurement;
    return [
      formatValue(powerValue, "kW"),
      formatValue(energyValue, "kWh"),
      currentStateOfCharge !== null && currentStateOfCharge !== undefined
        ? `SoC ${currentStateOfCharge}`
        : null,
    ].filter((part): part is string => part !== null);
  }
  const { transaction } = row;
  return [
    transaction.transaction_state,
    `${transaction.event_count} event${transaction.event_count === 1 ? "" : "s"}`,
    `latest: ${transaction.latest_event_name}`,
  ];
};

const columns: ColumnsType<Row> = [
  {
    title: "Time",
    dataIndex: "timestamp",
    key: "timestamp",
    width: 170,
    render: (timestamp: string) => (
      <span className="whitespace-nowrap">
        {dayjs(timestamp).format(MESSAGE_TIME_FORMAT)}
      </span>
    ),
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    render: (type: DataType) => (
      <Tag color={DATA_TYPE_TAGS[type].color}>{DATA_TYPE_TAGS[type].label}</Tag>
    ),
  },
  {
    title: "Asset",
    dataIndex: "asset_id",
    key: "asset_id",
    render: (assetId: string) => <Tag>{assetId}</Tag>,
  },
  {
    title: "Transaction",
    key: "transaction_id",
    render: (_, row) =>
      row.type === "transactions" ? (
        <Typography.Text code className="whitespace-nowrap">
          {row.transaction.transaction_id}
        </Typography.Text>
      ) : (
        "—"
      ),
  },
  {
    title: "Details",
    key: "summary",
    render: (_, row) => (
      <Space size={[4, 4]} wrap>
        {summarise(row).map((part) => (
          <Tag key={part} bordered={false}>
            {part}
          </Tag>
        ))}
      </Space>
    ),
  },
];

// The type and asset filters sit in the column headers. They stay
// server-side: the selection lives in the URL and decides what is fetched.
const withHeaderFilters = (
  types: DataType[],
  assets: string[],
  mappedAssets: string[],
): ColumnsType<Row> =>
  columns.map((column) => {
    switch (column.key) {
      case "type":
        return {
          ...column,
          filters: DATA_TYPES.map((type) => ({
            text: DATA_TYPE_TAGS[type].label,
            value: type,
          })),
          filteredValue: types,
        };
      case "asset_id":
        return {
          ...column,
          filters: mappedAssets.map((assetId) => ({
            text: assetId,
            value: assetId,
          })),
          filteredValue: assets,
        };
      default:
        return column;
    }
  });

const RowDetails = ({ row }: { row: Row }) => {
  const body = JSON.stringify(
    row.type === "measurements" ? row.measurement : row.transaction,
    null,
    2,
  );
  return (
    <Typography.Paragraph copyable={{ text: body }} className="mb-0">
      <pre className="m-0 max-h-96 overflow-auto rounded bg-gray-50 p-3 text-xs whitespace-pre-wrap break-all">
        {body}
      </pre>
    </Typography.Paragraph>
  );
};

type SocketDataLogProps = {
  site_id: string;
  socket_id: string;
  // Prefix of the URL search params holding the filters.
  filterPrefix?: string;
};

// The measurements and transactions stored for the assets this socket is
// currently mapped to: what the broker (or an MQTT integration) sent for them.
export const SocketDataLog = ({
  site_id,
  socket_id,
  filterPrefix,
}: SocketDataLogProps) => {
  const filters = useOcppMessageFilters(filterPrefix);
  const [searchDraft, setSearchDraft] = useState(filters.search);

  useEffect(() => setSearchDraft(filters.search), [filters.search]);

  const { data: configuration, isLoading: configurationLoading } = useQuery({
    ...getOcppConfigurationOptions({ path: { site_id } }),
    retry: false,
  });
  const mappedAssets = useMemo(
    () => [...(configuration?.socket_to_asset_map?.[socket_id] ?? [])].sort(),
    [configuration, socket_id],
  );
  const assets = mappedAssets.filter(
    (assetId) =>
      filters.assets.length === 0 || filters.assets.includes(assetId),
  );

  const showType = (type: DataType) =>
    filters.types.length === 0 || filters.types.includes(type);
  // Measurements have no transaction id, so a search only shows transactions.
  const showMeasurements = showType("measurements") && !filters.search;
  const showTransactions = showType("transactions");

  const { after: start } = filters.receivedRange;
  const stop = useMemo(
    () => filters.receivedRange.before ?? new Date().toISOString(),
    // A preset range moves with every refresh, which changes `after`.
    [filters.receivedRange],
  );

  const measurementQueries = useQueries({
    queries: assets.map((asset_id) => ({
      ...getMeasurementsForAssetOptions({
        path: { site_id, asset_id },
        query: { start, stop },
      }),
      enabled: showMeasurements,
    })),
  });

  const transactions = useInfiniteQuery({
    ...getTransactionsWithEventsForSiteInfiniteOptions({
      path: { site_id },
      query: {
        page_size: FETCH_PAGE_SIZE,
        asset_id: assets,
        begin: start,
        end: stop,
        // Accept both the Pleevi transaction id and the OCPP one.
        transaction_id: filters.search
          ? [
              filters.search,
              ...assets.map((assetId) => `${assetId}-${filters.search}`),
            ]
          : null,
      },
    }),
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page ? lastPage.end_cursor : undefined,
    enabled: showTransactions && assets.length > 0,
  });

  const rows = useMemo(() => {
    const measurementRows: Row[] = showMeasurements
      ? measurementQueries.flatMap(({ data }) =>
          (data ?? []).map((measurement) => ({
            key: `m-${measurement.source.asset_id}-${measurement.timestamp}`,
            type: "measurements" as const,
            timestamp: measurement.timestamp,
            asset_id: measurement.source.asset_id,
            measurement,
          })),
        )
      : [];
    const transactionRows: Row[] = showTransactions
      ? (transactions.data?.pages ?? []).flatMap((page) =>
          page.data.map((transaction) => ({
            key: `t-${transaction.transaction_id}`,
            type: "transactions" as const,
            timestamp: transaction.latest_event_received_at,
            asset_id: transaction.asset_id,
            transaction,
          })),
        )
      : [];
    return [...measurementRows, ...transactionRows].sort((a, b) =>
      b.timestamp.localeCompare(a.timestamp),
    );
  }, [
    measurementQueries,
    transactions.data,
    showMeasurements,
    showTransactions,
  ]);

  const error =
    measurementQueries.find((query) => query.error)?.error ??
    transactions.error;
  const isLoading =
    configurationLoading ||
    measurementQueries.some((query) => query.isLoading) ||
    transactions.isLoading;
  const isFetching =
    measurementQueries.some((query) => query.isFetching) ||
    transactions.isFetching;

  const handleRefresh = () => {
    if (filters.preset) {
      filters.refresh();
    } else {
      measurementQueries.forEach((query) => void query.refetch());
      void transactions.refetch();
    }
  };

  const handleTableChange: TableProps<Row>["onChange"] = (
    _pagination,
    tableFilters,
    _sorter,
    { action },
  ) => {
    if (action !== "filter") {
      return;
    }
    filters.update({
      types: (tableFilters.type ?? []).map(String).filter(isDataType),
      assets: (tableFilters.asset_id ?? []).map(String),
    });
  };

  if (!configurationLoading && mappedAssets.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="This socket is not mapped to any assets. Map its connectors to assets in the socket-to-asset map first."
      />
    );
  }

  return (
    <div className="w-full">
      <Space wrap className="mb-4">
        <Input.Search
          allowClear
          className="w-72"
          placeholder="Pleevi or OCPP transaction id"
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          onSearch={(value) => filters.update({ search: value.trim() })}
        />
        <MessageRangeFilter filters={filters} />
        <Button
          icon={<ReloadOutlined />}
          loading={isFetching && !transactions.isFetchingNextPage}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Space>

      {!!error && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Failed to load measurements or transactions"
          description={extractErrorMessage(error)}
        />
      )}

      <Table<Row>
        size="small"
        columns={withHeaderFilters(filters.types, filters.assets, mappedAssets)}
        onChange={handleTableChange}
        dataSource={rows}
        loading={isLoading}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        rowKey="key"
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (row) => <RowDetails row={row} />,
        }}
        locale={{
          emptyText:
            "No measurements or transactions match these filters. Try a wider time range.",
        }}
      />

      {showTransactions && transactions.hasNextPage && (
        <div className="mt-2 flex justify-center">
          <Button
            loading={transactions.isFetchingNextPage}
            onClick={() => void transactions.fetchNextPage()}
          >
            Load more transactions
          </Button>
        </div>
      )}
    </div>
  );
};
